'use client';

import { useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCentrifugo } from '../contexts/CentrifugoContext';
import { RECENT_DROPS_LIMIT } from '../lib/queryClient';
import {
  booleanField,
  nullableStringField,
  literalField,
  numberField,
  rawField,
  stringField,
} from '../lib/jsonBody';
import {
  ItemSource,
  type ItemResponse,
  type RecentInventoryItem,
} from '../services/generated/Api';

const CHANNEL_DROPS_FEED_SUFFIX = 'drops_feed';

const getDropsFeedChannel = (prefix: string): string => {
  return prefix ? `${prefix}_${CHANNEL_DROPS_FEED_SUFFIX}` : CHANNEL_DROPS_FEED_SUFFIX;
};

const RECENT_DROPS_KEY = ['home', 'recentDrops'] as const;

const DROP_SOURCES = Object.values(ItemSource);

const parseItem = (value: unknown): ItemResponse | null => {
  const hashName = stringField(value, 'hashName');
  const name = stringField(value, 'name');
  const statTrak = booleanField(value, 'statTrak');
  const souvenir = booleanField(value, 'souvenir');
  if (hashName === undefined || name === undefined || statTrak === undefined || souvenir === undefined) {
    return null;
  }
  const viewerData = rawField(value, 'viewerData');
  return {
    hashName,
    name,
    statTrak,
    souvenir,
    rarityId: nullableStringField(value, 'rarityId'),
    rarityName: nullableStringField(value, 'rarityName'),
    rarityColor: nullableStringField(value, 'rarityColor'),
    weapon: nullableStringField(value, 'weapon'),
    category: nullableStringField(value, 'category'),
    wear: nullableStringField(value, 'wear'),
    imageUrl: nullableStringField(value, 'imageUrl'),
    type: nullableStringField(value, 'type'),
    priceInRubles: numberField(value, 'priceInRubles'),
    priceInUsd: numberField(value, 'priceInUsd'),
    steamPrice: numberField(value, 'steamPrice') ?? null,
    viewerData: typeof viewerData === 'object' && viewerData !== null ? viewerData : null,
  };
};

export const parseDrop = (data: unknown): RecentInventoryItem | null => {
  const id = stringField(data, 'id');
  const userId = stringField(data, 'userId');
  const acquiredPriceInCredits = numberField(data, 'acquiredPriceInCredits');
  const acquiredAt = stringField(data, 'acquiredAt');
  const source = literalField(data, 'source', DROP_SOURCES);
  const item = parseItem(rawField(data, 'item'));
  if (
    id === undefined ||
    userId === undefined ||
    acquiredPriceInCredits === undefined ||
    acquiredAt === undefined ||
    source === undefined ||
    item === null
  ) {
    return null;
  }
  return {
    id,
    userId,
    acquiredPriceInCredits,
    acquiredAt,
    source,
    sourceId: nullableStringField(data, 'sourceId'),
    item,
  };
};

export const useTopDropsSubscription = () => {
  const { subscribe, isConnected, channelPrefix } = useCentrifugo();
  const queryClient = useQueryClient();

  const channelName = useMemo(
    () => getDropsFeedChannel(channelPrefix),
    [channelPrefix],
  );

  const handleNewDrop = useCallback(
    (data: unknown) => {
      const drop = parseDrop(data);
      if (!drop) {
        queryClient.invalidateQueries({ queryKey: RECENT_DROPS_KEY });
        return;
      }

      queryClient.setQueryData<RecentInventoryItem[]>(RECENT_DROPS_KEY, (oldDrops = []) => {
        if (oldDrops.some((d) => d.id === drop.id)) return oldDrops;
        return [drop, ...oldDrops.slice(0, RECENT_DROPS_LIMIT - 1)];
      });
    },
    [queryClient],
  );

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe(channelName, handleNewDrop);

    return unsubscribe;
  }, [isConnected, channelName, subscribe, handleNewDrop]);

  return { isConnected };
};
